// Model prices and the one pricing function every part of the system uses
// (otto-app's cost ledger, otto-live's in-call voice meter). Prices are
// entered by a person, per MILLION tokens; the table ships empty (the agency
// cost gate). A call whose model, or any part it used, has no price is
// UNKNOWN (null), never $0.
import { z } from "zod";

const PerMillion = z.number().min(0.01).max(1000);

/** One model's prices, dollars per million tokens. The floor refuses a per-token figure typed by mistake. */
export const ModelPrice = z.strictObject({
  input: PerMillion,
  output: PerMillion,
  cached_input: PerMillion.optional(),
  cache_write: PerMillion.optional(),
  audio_input: PerMillion.optional(),
  audio_output: PerMillion.optional(),
  /** Who entered it and from where (a pricing page and its date). */
  source: z.string().min(1).max(300),
});
export type ModelPrice = z.infer<typeof ModelPrice>;

/** Token counts by priced part. Absent parts were not used. */
export interface TokenUsage {
  input?: number;
  output?: number;
  cached_input?: number;
  cache_write?: number;
  audio_input?: number;
  audio_output?: number;
}

export const PRICED_PARTS = ["input", "output", "cached_input", "cache_write", "audio_input", "audio_output"] as const;

/**
 * Price a call. null when there is no price for the model, or when any part
 * with tokens has no price (a partial price is still unknown).
 */
export function priceUsage(usage: TokenUsage, price: ModelPrice | null | undefined): number | null {
  if (!price) return null;
  let total = 0;
  for (const part of PRICED_PARTS) {
    const tokens = usage[part] ?? 0;
    if (tokens <= 0) continue;
    const perMillion = price[part];
    if (perMillion === undefined) return null;
    total += (tokens * perMillion) / 1_000_000;
  }
  // Round to a millionth of a dollar without losing small calls.
  return Math.round(total * 1_000_000) / 1_000_000;
}
